import React from 'react';
import { PageHeader } from '@components/layout/Breadcrumb';
import SectionHeader from '@components/common/SectionHeader';
import Card from '@components/common/Card';
import { FiUsers, FiAward, FiTrendingUp, FiHeart } from 'react-icons/fi';
import {
  COMPANY_INFO,
  MISSION,
  VISION,
  VALUES,
  MEMBER_COOPERATIVES,
} from '@utils/constants';

/**
 * About Page Component
 * Complete company information, history, team, and cooperatives
 */
const About = () => {
  const breadcrumbs = [{ label: 'About Us', path: '/about' }];

  return (
    <div className="about-page">
      {/* Page Header */}
      <PageHeader
        title="About Bukawa ACE Ltd"
        description="Empowering farmers and transforming agriculture in Eastern Uganda since 2003"
        breadcrumbs={breadcrumbs}
      />

      {/* Company Overview Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <SectionHeader
                subtitle="Who We Are"
                title="A Cooperative Built on Trust and Quality"
                centered={false}
                className="mb-6"
              />

              <div className="prose prose-lg max-w-none">
                <p className="text-bukawa-gray-700 leading-relaxed mb-4">
                  <strong className="text-bukawa-primary">
                    {COMPANY_INFO.name}
                  </strong>{' '}
                  is a secondary cooperative organization that was established in{' '}
                  {COMPANY_INFO.yearEstablished} and officially registered by the
                  Registrar of Cooperatives on {COMPANY_INFO.registrationDate} with
                  Registration Number <strong>{COMPANY_INFO.registrationNumber}</strong>.
                </p>

                <p className="text-bukawa-gray-700 leading-relaxed mb-4">
                  We are located in Nakisenyi Village, Wairama Parish, Nakigo
                  Sub-County, Kigulu South, Iganga District, in Eastern Uganda. Our
                  cooperative brings together{' '}
                  <strong className="text-bukawa-primary">
                    {COMPANY_INFO.totalMembers} individual farmer members
                  </strong>{' '}
                  and{' '}
                  <strong className="text-bukawa-primary">
                    {COMPANY_INFO.totalCooperatives} primary cooperative societies
                  </strong>{' '}
                  and rural producer organizations.
                </p>

                <p className="text-bukawa-gray-700 leading-relaxed">
                  Through collective action, we increase household incomes by focusing
                  on commercial production, post-harvest handling, bulking, value
                  addition, and collective marketing of agricultural products—primarily
                  premium white maize flour.
                </p>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-bukawa-gray-200">
                <div>
                  <div className="text-3xl font-bold text-bukawa-primary mb-1">
                    {COMPANY_INFO.yearEstablished}
                  </div>
                  <div className="text-sm text-bukawa-gray-600">Established</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-bukawa-primary mb-1">
                    {COMPANY_INFO.totalMembers}+
                  </div>
                  <div className="text-sm text-bukawa-gray-600">Members</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-bukawa-primary mb-1">
                    {COMPANY_INFO.totalCooperatives}
                  </div>
                  <div className="text-sm text-bukawa-gray-600">Cooperatives</div>
                </div>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-large">
                <img
                  src="/ooopHQ.jpeg"
                  alt="Bukawa Headquarters"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-bukawa-secondary rounded-full opacity-10 blur-3xl" />
              <div className="absolute -top-6 -right-6 w-40 h-40 bg-bukawa-primary rounded-full opacity-10 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="section bg-bukawa-light">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Purpose"
            title="Mission, Vision & Values"
            className="mb-16"
          />

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Mission */}
            <Card className="bg-white border-2 border-bukawa-primary-100">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-bukawa-primary text-white rounded-xl flex items-center justify-center">
                  <FiAward className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-bukawa-primary mb-2">
                    Our Mission
                  </h3>
                </div>
              </div>
              <p className="text-bukawa-gray-700 leading-relaxed text-lg">
                {MISSION}
              </p>
            </Card>

            {/* Vision */}
            <Card className="bg-white border-2 border-bukawa-secondary-100">
              <div className="flex items-start gap-4 mb-4">
                <div className="flex-shrink-0 w-14 h-14 bg-bukawa-secondary text-bukawa-primary-900 rounded-xl flex items-center justify-center">
                  <FiTrendingUp className="h-7 w-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-bukawa-primary mb-2">
                    Our Vision
                  </h3>
                </div>
              </div>
              <p className="text-bukawa-gray-700 leading-relaxed text-lg">
                {VISION}
              </p>
            </Card>
          </div>

          {/* Values */}
          <div>
            <h3 className="text-2xl font-heading font-bold text-bukawa-gray-900 text-center mb-8">
              Our Core Values
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {VALUES.map((value) => (
                <Card
                  key={value.id}
                  hover
                  className="bg-white text-center border border-bukawa-gray-200"
                >
                  <div className="w-16 h-16 bg-bukawa-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiHeart className="h-8 w-8 text-bukawa-primary" />
                  </div>
                  <h4 className="text-xl font-semibold text-bukawa-gray-900">
                    {value.name}
                  </h4>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Board Members Section */}
      <BoardMembersSection />

      {/* Staff Members Section */}
      <StaffMembersSection />

      {/* Member Cooperatives Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <SectionHeader
            subtitle="Our Network"
            title="Member Cooperatives & Organizations"
            description="We are proud to work with 10 primary cooperatives and rural producer organizations"
            className="mb-16"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MEMBER_COOPERATIVES.map((coop) => (
              <Card
                key={coop.id}
                hover
                className="bg-bukawa-light border border-bukawa-gray-200"
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-bukawa-primary text-white rounded-lg flex items-center justify-center font-bold">
                    {coop.id}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-bukawa-gray-900 leading-tight">
                      {coop.name}
                    </h4>
                  </div>
                </div>
                <p className="text-sm text-bukawa-gray-600 bg-white px-3 py-2 rounded-lg inline-block">
                  {coop.type}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* History Timeline Section */}
      <HistorySection />
    </div>
  );
};

/**
 * Board Members Section Component
 */
const BoardMembersSection = () => {
  const boardMembers = [
    {
      id: 1,
      name: 'Mr. Kayiira Edward',
      title: 'Chairperson',
      gender: 'Male',
    },
    {
      id: 2,
      name: 'Mrs. Nabirye Christine',
      title: 'Vice Chairperson',
      gender: 'Female',
    },
    {
      id: 3,
      name: 'Mr. Kisubi Muzamiru',
      title: 'Secretary',
      gender: 'Male',
    },
    {
      id: 4,
      name: 'Mrs. Takwemazayo Salima',
      title: 'Treasurer',
      gender: 'Female',
    },
    {
      id: 5,
      name: 'Mrs. Baleese Bayati Kagoya',
      title: 'Board Member',
      gender: 'Female',
    },
  ];

  return (
    <section className="section bg-bukawa-cream">
      <div className="container-custom">
        <SectionHeader
          subtitle="Leadership"
          title="Board of Directors"
          description="Experienced leaders guiding our cooperative towards excellence"
          className="mb-16"
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {boardMembers.map((member) => (
            <Card
              key={member.id}
              hover
              className="text-center bg-white border border-bukawa-gray-200"
            >
              {/* Avatar Placeholder */}
              <div className="w-24 h-24 bg-bukawa-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiUsers className="h-12 w-12 text-bukawa-primary" />
              </div>

              {/* Name */}
              <h4 className="font-semibold text-bukawa-gray-900 mb-1">
                {member.name}
              </h4>

              {/* Title */}
              <p className="text-sm text-bukawa-primary font-medium mb-2">
                {member.title}
              </p>

              {/* Gender Badge */}
              <span className="inline-block px-3 py-1 bg-bukawa-light text-bukawa-gray-700 text-xs rounded-full">
                {member.gender}
              </span>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Staff Members Section Component
 */
const StaffMembersSection = () => {
  const staffMembers = [
    { id: 1, name: 'Mr. Bogere David', title: 'Manager', gender: 'Male' },
    { id: 2, name: 'Mr. Bogere Hakim', title: 'Accountant', gender: 'Male' },
    { id: 3, name: 'Mr. Kigenyi Hassan', title: 'Project Officer', gender: 'Male' },
    { id: 4, name: 'Mrs. Kyobiika Betty', title: 'Agriculture Extension Officer', gender: 'Female' },
    { id: 5, name: 'Mr. Tigatoola Farouk', title: 'Store Keeper', gender: 'Male' },
    { id: 6, name: 'Mr. Kisambira Ivan', title: 'Accounts Assistant', gender: 'Male' },
    { id: 7, name: 'Miss. Nabirye Sophia', title: 'Shop Attendant', gender: 'Female' },
    { id: 8, name: 'Mr. Basoga Muhamudu', title: 'Security', gender: 'Male' },
  ];

  return (
    <section className="section bg-white">
      <div className="container-custom">
        <SectionHeader
          subtitle="Our Team"
          title="Staff Members"
          description="Dedicated professionals committed to serving our cooperative members"
          className="mb-16"
        />

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {staffMembers.map((member) => (
            <Card
              key={member.id}
              hover
              className="text-center bg-bukawa-light border border-bukawa-gray-200"
            >
              {/* Avatar */}
              <div className="w-20 h-20 bg-bukawa-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-2xl font-bold text-bukawa-secondary-700">
                  {member.name.charAt(0)}
                </span>
              </div>

              {/* Name */}
              <h4 className="font-semibold text-bukawa-gray-900 text-sm mb-1">
                {member.name}
              </h4>

              {/* Title */}
              <p className="text-xs text-bukawa-gray-600">{member.title}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * History Timeline Section Component
 */
const HistorySection = () => {
  const timeline = [
    {
      year: 2003,
      title: 'Cooperative Established',
      description: 'Bukawa Area Marketing Enterprise Cooperative Society was founded by local farmers.',
    },
    {
      year: 2004,
      title: 'Official Registration',
      description: 'Registered by the Registrar of Cooperatives on January 16, 2004 (Reg No. 6952).',
    },
    {
      year: 2020,
      title: 'Agriculture Cluster Development',
      description: 'Received 281 million UGX for post-harvest handling and processing from MAAIF.',
    },
    {
      year: 2023,
      title: '20 Years of Service',
      description: 'Celebrating two decades of empowering farmers and producing quality maize flour.',
    },
    {
      year: 2024,
      title: 'Continued Growth',
      description: 'Serving over 1,052 farmers across 10 cooperatives with premium certified products.',
    },
  ];

  return (
    <section className="section bg-bukawa-light">
      <div className="container-custom">
        <SectionHeader
          subtitle="Our Journey"
          title="History & Milestones"
          description="Two decades of growth, innovation, and farmer empowerment"
          className="mb-16"
        />

        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {timeline.map((event, index) => (
              <div key={index} className="flex gap-6">
                {/* Year */}
                <div className="flex-shrink-0 w-24">
                  <div className="text-3xl font-bold text-bukawa-primary">
                    {event.year}
                  </div>
                </div>

                {/* Timeline Line */}
                <div className="flex-shrink-0 flex flex-col items-center">
                  <div className="w-4 h-4 bg-bukawa-secondary rounded-full" />
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 flex-1 bg-bukawa-gray-300 my-2" />
                  )}
                </div>

                {/* Content */}
                <Card className="flex-1 bg-white mb-8">
                  <h4 className="text-xl font-semibold text-bukawa-gray-900 mb-2">
                    {event.title}
                  </h4>
                  <p className="text-bukawa-gray-600">{event.description}</p>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;